/**
 * Provider Usage mock (CT-4182) — prototype `MOCK_CONSUMERS` + `getImpactSummary`.
 * Slack matches `#/integrations/slack` Usage: 13 workflows · 6 agents · 4 networks · 1 widget.
 */

const STUDIO_BY_TYPE = {
  workflow: 'agentic',
  agent: 'agentic',
  network: 'agentic',
  widget: 'workforce',
};

/** Prototype consumer catalog. `integrations` lists provider slugs that depend on it. */
export const USAGE_CONSUMERS = [
  { name: 'New lead → Salesforce + Slack alert', type: 'workflow', owner: 'María González', integrations: ['salesforce', 'slack'], uses: 142, source: 'Engineering channels', lastActivity: '4 min ago' },
  { name: 'Failed payment escalation', type: 'workflow', owner: 'Alex Park', integrations: ['stripe', 'slack', 'gmail'], uses: 18 },
  { name: 'GitHub PR review notification', type: 'workflow', owner: 'Carlos Tovar', integrations: ['github', 'slack'], uses: 87, source: 'AIMS Workspace', lastActivity: '12 min ago' },
  { name: 'CS ticket auto-routing', type: 'workflow', owner: 'Sarah Chen', integrations: ['zendesk', 'slack'], uses: 64 },
  { name: 'Daily revenue report', type: 'workflow', owner: 'Alex Park', integrations: ['stripe', 'gsheets'], uses: 7 },
  { name: 'Inbound demo → Calendar invite', type: 'workflow', owner: 'María González', integrations: ['hubspot', 'gcal', 'gmail'], uses: 32 },
  { name: 'Lead enrichment from Clearbit', type: 'workflow', owner: 'María González', integrations: ['hubspot', 'salesforce', 'slack'], uses: 48 },
  { name: 'Renewal risk alert', type: 'workflow', owner: 'Alex Park', integrations: ['salesforce', 'slack', 'stripe'], uses: 23 },
  { name: 'Stale opportunity nudge', type: 'workflow', owner: 'María González', integrations: ['salesforce', 'slack'], uses: 14 },
  { name: 'New employee onboarding kickoff', type: 'workflow', owner: 'Sarah Chen', integrations: ['workday', 'slack', 'gmail'], uses: 5 },
  { name: 'Incident response · Sev1', type: 'workflow', owner: 'Carlos Tovar', integrations: ['github', 'slack', 'datadog'], uses: 11 },
  { name: 'Weekly churn digest', type: 'workflow', owner: 'Sarah Chen', integrations: ['salesforce', 'gmail'], uses: 2 },
  { name: 'Expense report approval', type: 'workflow', owner: 'Alex Park', integrations: ['slack', 'notion'], uses: 38 },
  { name: 'Customer NPS follow-up', type: 'workflow', owner: 'Sarah Chen', integrations: ['zendesk', 'gmail'], uses: 19 },
  { name: 'PR merge → deploy preview', type: 'workflow', owner: 'Carlos Tovar', integrations: ['github', 'slack'], uses: 52 },
  { name: 'Data quality nightly check', type: 'workflow', owner: 'Naomi Park', integrations: ['snowflake', 'slack'], uses: 7 },
  { name: 'Slack channel auto-archive (30d)', type: 'workflow', owner: 'Thomas González', integrations: ['slack'], uses: 4 },
  { name: 'Sales Triage Agent', type: 'agent', owner: 'María González', integrations: ['salesforce', 'hubspot', 'slack'], uses: 24 },
  { name: 'Support Resolution Agent', type: 'agent', owner: 'Sarah Chen', integrations: ['zendesk', 'slack', 'notion'], uses: 11 },
  { name: 'Eng Standup Agent', type: 'agent', owner: 'Carlos Tovar', integrations: ['github', 'slack', 'notion'], uses: 18 },
  { name: 'Renewals Agent', type: 'agent', owner: 'Alex Park', integrations: ['salesforce', 'hubspot', 'stripe'], uses: 6 },
  { name: 'PR Reviewer Agent', type: 'agent', owner: 'Carlos Tovar', integrations: ['github', 'slack'], uses: 14 },
  { name: 'Knowledge Indexer Agent', type: 'agent', owner: 'Naomi Park', integrations: ['notion', 'gdrive', 'slack'], uses: 8 },
  { name: 'Inbound Demo Booker', type: 'agent', owner: 'María González', integrations: ['hubspot', 'gcal', 'gmail'], uses: 9 },
  { name: 'Finance Approval Agent', type: 'agent', owner: 'Alex Park', integrations: ['slack', 'notion'], uses: 4 },
  { name: 'CS Health Score Agent', type: 'agent', owner: 'Sarah Chen', integrations: ['salesforce', 'zendesk'], uses: 12 },
  { name: 'Customer Onboarding Network', type: 'network', owner: 'Sarah Chen', integrations: ['hubspot', 'salesforce', 'slack', 'zendesk'], uses: 4 },
  { name: 'Incident Response Network', type: 'network', owner: 'Carlos Tovar', integrations: ['github', 'slack', 'snowflake'], uses: 3 },
  { name: 'Revenue Operations Network', type: 'network', owner: 'María González', integrations: ['salesforce', 'hubspot', 'stripe', 'slack'], uses: 5 },
  { name: 'Engineering Velocity Network', type: 'network', owner: 'Carlos Tovar', integrations: ['github', 'linear', 'slack'], uses: 3 },
  { name: 'Pipeline Health Dashboard', type: 'widget', owner: 'María González', integrations: ['salesforce', 'hubspot'], uses: 412 },
  { name: 'CS SLA Tracker', type: 'widget', owner: 'Sarah Chen', integrations: ['zendesk', 'salesforce'], uses: 89 },
  { name: 'Eng Velocity Dashboard', type: 'widget', owner: 'Carlos Tovar', integrations: ['github', 'linear'], uses: 127 },
  { name: 'Renewal Forecast Widget', type: 'widget', owner: 'Alex Park', integrations: ['salesforce', 'stripe'], uses: 64 },
  { name: 'Top Accounts Card', type: 'widget', owner: 'María González', integrations: ['salesforce'], uses: 152 },
  { name: 'Active Incidents Panel', type: 'widget', owner: 'Carlos Tovar', integrations: ['github', 'slack'], uses: 201 },
  { name: 'CSAT Trend Widget', type: 'widget', owner: 'Sarah Chen', integrations: ['zendesk'], uses: 73 },
];

const SOURCE_CHANNELS = ['Engineering channels', 'AIMS Workspace', 'Sales alerts', 'Support ops'];
const LAST_ACTIVITY = ['4 min ago', '12 min ago', '1h ago', '3h ago', '8h ago', 'yesterday'];

function pickFromSeed(list, seed) {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash + seed.charCodeAt(i)) % list.length;
  return list[hash];
}

const toRow = (item) => ({
  name: item.name,
  type: item.type,
  uses: item.uses,
  studio: STUDIO_BY_TYPE[item.type] ?? 'agentic',
  owner: item.owner,
  source: item.source ?? pickFromSeed(SOURCE_CHANNELS, item.name),
  lastActivity: item.lastActivity ?? pickFromSeed(LAST_ACTIVITY, `${item.name}:${item.owner}`),
});

/**
 * Usage payload for a provider slug (prototype `getImpactSummary`).
 *
 * @param {string} slug
 */
export const buildProviderUsage = (slug) => {
  const key = String(slug ?? '').toLowerCase();
  const consumers = USAGE_CONSUMERS.filter((c) => c.integrations.includes(key)).map(toRow);
  const byType = {
    workflows: consumers.filter((c) => c.type === 'workflow').length,
    agents: consumers.filter((c) => c.type === 'agent').length,
    networks: consumers.filter((c) => c.type === 'network').length,
    widgets: consumers.filter((c) => c.type === 'widget').length,
  };
  return {
    stats: {
      consumer_count: consumers.length,
      by_type: byType,
    },
    consumers,
  };
};
